import React from 'react';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import MaskedInput from 'react-text-mask';

import PropTypes from 'prop-types';

import Card from '@/components/common/Card';
import Radio from '@/components/common/elements/Radio';
import clientImage from '@/components/common/icons/client-circle.svg';
import detailsImage from '@/components/common/icons/client-details.svg';
import contactImage from '@/components/common/icons/contact-circle.svg';
import documentImage from '@/components/common/icons/document-circle.svg';
import Spinner from '@/components/common/Spinner';
import { USERNAME_MASK } from '@/helpers/mask';
import AddressIcon from '@/svg-icons/address';
import ClientIcon from '@/svg-icons/client';

const SectionTitle = ({ icon, children, isFirst }) => (
  <h5 className={`flex items-center mb-2 text-[0.93875em] !font-bold leading-[1.1] text-[#393939] ${isFirst ? 'mt-0' : 'mt-[1.5em]'}`}>
    <div className="w-[24px] h-[24px] mr-[0.5em] flex items-center justify-center flex-shrink-0">
      {typeof icon === 'string' ? (
        <img src={icon} alt="" className="h-[20px] w-auto" />
      ) : (
        React.cloneElement(icon, { className: 'h-[20px] w-auto' })
      )}
    </div>
    {children}
  </h5>
);

SectionTitle.propTypes = {
  icon: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
  isFirst: PropTypes.bool,
};

SectionTitle.defaultProps = {
  isFirst: false,
};

const ClientRegistrationFormComponent = ({
  busy,
  client,
  franchisees,
  loading,
  intl,
  showBillingAddress,
  origins,
  classifications,
  zipcodeMask,
  states,
  cities,
  billingCities,
  neighborhoods,
  billingNeighborhoods,
  type,
  onChange,
  onChangeFranchisee,
  onSubmit,
  cnpjMask,
  cpfOnlyMask,
  phoneMask,
  cellphoneMask,
  cellphoneMaskLength,
  useTextInputForAddress = false,
}) => {
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '34px',
      borderRadius: '3px',
      borderColor: state.isFocused ? '#4d8fca' : '#e3e3e3',
      boxShadow: 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#4d8fca' : '#e3e3e3',
      },
    }),
    valueContainer: (base) => ({
      ...base,
      maxWidth: 'calc(100% - 73px)',
      padding: '0 8px',
      '& [class$="Input"]': {
        padding: 0,
        lineHeight: 0,
      },
      '& .select__input': {
        padding: '4px 0',
        height: '24px',
        '& input': {
          height: '100% !important',
          background: 'transparent !important',
          border: 'none !important',
          boxShadow: 'none !important',
          padding: '0 !important',
          margin: '0 !important',
        },
      },
    }),
    placeholder: (base) => ({
      ...base,
      fontSize: '12px',
      color: '#b1b1b1',
    }),
    singleValue: (base) => ({
      ...base,
      fontSize: '12px',
      color: '#919191',
      fontWeight: '600',
    }),
    input: (base) => ({
      ...base,
      fontSize: '12px',
    }),
    menu: (base) => ({
      ...base,
      fontSize: '12px',
      '& .select__menu-list': {
        maxHeight: '220px',
        height: '220px',
        '& .select__option': {
          fontSize: '12px',
          cursor: 'pointer',
        },
        '& .select__menu-notice--no-options': {
          fontSize: '12px',
        },
      },
    }),
    multiValue: (base) => ({
      ...base,
      borderRadius: '15px',
      backgroundColor: '#658ec6',
      color: '#ffffff',
    }),
    multiValueLabel: (base) => ({
      ...base,
      padding: '2px 2px 2px 8px',
      fontSize: '12px',
      color: '#ffffff',
    }),
    multiValueRemove: (base) => ({
      ...base,
      borderRadius: '0 13px 13px 0',
      padding: '2px 5px 2px 2px',
      backgroundColor: '#658ec6',
      cursor: 'pointer',
      transition: 'background-color 0.3s',
      '&:hover': {
        backgroundColor: '#5079b3',
        color: '#ffffff',
      },
    }),
    indicator: (base) => ({
      ...base,
      padding: '6px',
    }),
  };

  const inputClasses = "h-[3em] px-[0.5em] border border-[#e3e3e3] rounded-[3px] bg-white text-[0.875em] font-semibold text-[#919191] transition-colors focus:outline-none focus:border-[#4d8fca] placeholder:font-normal placeholder:text-[#b1b1b1]";
  const labelClasses = "text-[0.75em] font-bold uppercase text-[#393939]";
  const fieldClasses = "flex flex-col mt-[1.25em] px-[3.46em] first:mt-0";
  const fieldRowClasses = "flex flex-row justify-between mt-[1.25em] px-[3.46em] first:mt-0";
  const fieldColClasses = "flex flex-col mt-0 px-0";

  const isPJ = type === 'pj';

  if (loading) {
    return (
      <div className="flex flex-col p-[1.5em_1em]">
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col p-[1.5em_1em]">
      <Card className="fade-in !p-[1.75em_1.625em]">
        <h4 className="flex items-center mb-[1.4em] pb-[1.4em] border-b border-[#eee] text-[1.25em] font-bold leading-[1.3] text-[#393939] relative">
          <div className="mr-[0.5em] w-[24px] h-[24px] relative top-[2px] flex items-center justify-center">
            <Link to="/commercial/clients">
              <div className="relative left-[4px] w-[14px] h-[14px] border-l-2 border-b-2 border-[#393939] rotate-45" />
            </Link>
          </div>

          <div className="mr-[0.5em] w-[28px] h-auto flex items-center justify-center">
            <img src={detailsImage} alt={intl.formatMessage({ id: 'DETAILS' })} className="max-w-full max-h-[28px]" />
          </div>

          {intl.formatMessage({ id: 'DETAILS' })}
        </h4>

        <form onSubmit={onSubmit}>
          <SectionTitle icon={clientImage} isFirst>
            {intl.formatMessage({ id: 'CUSTOMER' })}
          </SectionTitle>

          <div className="flex flex-row items-center mt-[1.4em] mb-[2em] px-[3.46em]">
            <div className="flex items-center">
              <Radio
                name="client_type"
                className="mr-[0.5em]"
                id="clientTypePJ"
                data-cy="clientTypePJ"
                value="pj"
                onChange={onChange}
                checked={isPJ}
              />
              <label htmlFor="clientTypePJ" className="m-0 text-[0.93875em] font-medium text-[#393939] cursor-pointer">
                {intl.formatMessage({ id: 'LEGAL_PERSON' })}
              </label>
            </div>
            <div className="flex items-center ml-[2em]">
              <Radio
                name="client_type"
                className="mr-[0.5em]"
                id="clientTypePF"
                data-cy="clientTypePF"
                value="pf"
                onChange={onChange}
                checked={!isPJ}
              />
              <label htmlFor="clientTypePF" className="m-0 text-[0.93875em] font-medium text-[#393939] cursor-pointer">
                {intl.formatMessage({ id: 'NATURAL_PERSON' })}
              </label>
            </div>
          </div>

          <div className={fieldClasses}>
            <label className={labelClasses} htmlFor="name">
              {isPJ ? intl.formatMessage({ id: 'FANTASY_NAME' }) : intl.formatMessage({ id: 'NAME' })}*
            </label>
            <input className={inputClasses} type="text" name="name" id="name" required value={client?.name || ''} onChange={onChange} />
          </div>

          <div className={fieldRowClasses}>
            <div className={`${fieldColClasses} w-[calc(50%-5px)]`}>
              <label className={labelClasses} htmlFor="classification">{intl.formatMessage({ id: 'CLASSIFICATION' })}*</label>
              <select className={inputClasses} name="classification" id="classification" data-cy="classification" value={client?.classification || ''} required onChange={onChange}>
                <option value="" disabled />
                {classifications.map(classification => (
                  <option value={classification} key={classification}>
                    {intl.formatMessage({ id: `CLASSIFICATION.${classification}` })}
                  </option>
                ))}
              </select>
            </div>
            <div className={`${fieldColClasses} w-[calc(50%-5px)]`}>
              <label className={labelClasses} htmlFor="origin">{intl.formatMessage({ id: 'ORIGIN' })}*</label>
              <select className={inputClasses} name="origin" id="origin" data-cy="origin" value={client?.origin || ''} required onChange={onChange}>
                <option value="" disabled />
                {origins.map(origin => (
                  <option value={origin} key={origin}>
                    {intl.formatMessage({ id: `ORIGIN.${origin}` })}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={fieldRowClasses}>
            {isPJ ? (
              <>
                <div className={`${fieldColClasses} w-[calc(50%-5px)]`}>
                  <label className={labelClasses} htmlFor="cnpj">{intl.formatMessage({ id: 'CNPJ' })}*</label>
                  <MaskedInput className={inputClasses} type="text" name="document" id="cnpj" data-cy="cnpj" required value={client?.document || ''} onChange={onChange} guide={false} mask={cnpjMask} />
                </div>
                <div className={`${fieldColClasses} w-[calc(50%-5px)]`}>
                  <label className={labelClasses} htmlFor="billing-name">{intl.formatMessage({ id: 'COMPANY_NAME' })}*</label>
                  <input className={inputClasses} type="text" name="company_name" id="billing-name" data-cy="billing-name" required value={client?.company_name || ''} onChange={onChange} />
                </div>
              </>
            ) : (
              <div className={`${fieldColClasses} w-[calc(50%-5px)]`}>
                <label className={labelClasses} htmlFor="cpf">{intl.formatMessage({ id: 'CPF' })}*</label>
                <MaskedInput className={inputClasses} type="text" name="document" id="cpf" data-cy="cpf" required value={client?.document || ''} onChange={onChange} guide={false} mask={cpfOnlyMask} />
              </div>
            )}
          </div>

          <div className={fieldRowClasses}>
            <div className={`${fieldColClasses} w-[calc(50%-5px)]`}>
              <label className={labelClasses} htmlFor="username">{intl.formatMessage({ id: 'USER' })}*</label>
              <MaskedInput className={inputClasses} type="text" name="username" id="username" data-cy="username" required value={client?.username || ''} onChange={onChange} guide={false} mask={USERNAME_MASK} />
            </div>
            <div className={`${fieldColClasses} w-[calc(50%-5px)]`}>
              <label className={labelClasses} htmlFor="email">{intl.formatMessage({ id: 'EMAIL' })}*</label>
              <input className={inputClasses} type="text" name="contact[email]" id="email" data-cy="email" required value={client?.contact?.email || ''} onChange={onChange} />
            </div>
          </div>

          {isPJ && (
            <>
              <SectionTitle icon={<ClientIcon />}>
                {intl.formatMessage({ id: 'RESPONSIBLE' })}
              </SectionTitle>

              <div className={fieldClasses}>
                <label className={labelClasses} htmlFor="responsibleName">{intl.formatMessage({ id: 'NAME' })}*</label>
                <input className={inputClasses} type="text" name="responsible[name]" id="responsibleName" data-cy="responsibleName" value={client?.responsible?.name || ''} onChange={onChange} />
              </div>

              <div className={fieldRowClasses}>
                <div className={`${fieldColClasses} w-[calc(33.33%-6.67px)]`}>
                  <label className={labelClasses} htmlFor="responsibleDocument">{intl.formatMessage({ id: 'CPF' })}*</label>
                  <MaskedInput className={inputClasses} type="text" name="responsible[document]" id="responsibleDocument" data-cy="responsibleDocument" required value={client?.responsible?.document || ''} onChange={onChange} guide={false} mask={cpfOnlyMask} />
                </div>
                <div className={`${fieldColClasses} w-[calc(33.33%-6.67px)]`}>
                  <label className={labelClasses} htmlFor="responsiblePhone">{intl.formatMessage({ id: 'PHONE' })}</label>
                  <MaskedInput className={inputClasses} type="text" name="responsible[phone]" id="responsiblePhone" data-cy="responsiblePhone" value={client?.responsible?.phone || ''} onChange={onChange} guide={false} mask={value => (value.length >= cellphoneMaskLength ? cellphoneMask : phoneMask)} />
                </div>
                <div className={`${fieldColClasses} w-[calc(33.33%-6.67px)]`}>
                  <label className={labelClasses} htmlFor="responsibleCellphone">{intl.formatMessage({ id: 'CELLPHONE' })}</label>
                  <MaskedInput className={inputClasses} type="text" name="responsible[cellphone]" id="responsibleCellphone" data-cy="responsibleCellphone" value={client?.responsible?.cellphone || ''} onChange={onChange} guide={false} mask={value => (value.length >= cellphoneMaskLength ? cellphoneMask : phoneMask)} />
                </div>
              </div>

              <div className={fieldClasses}>
                <label className={labelClasses} htmlFor="ResponsibleEmail">{intl.formatMessage({ id: 'EMAIL' })}</label>
                <input className={inputClasses} type="text" name="responsible[email]" id="ResponsibleEmail" data-cy="ResponsibleEmail" value={client?.responsible?.email || ''} onChange={onChange} />
              </div>
            </>
          )}

          <SectionTitle icon={<AddressIcon />}>
            {intl.formatMessage({ id: 'CUSTOMER_ADDRESS' })}
          </SectionTitle>

          <div className={fieldRowClasses}>
            <div className={`${fieldColClasses} w-[calc(50%-5px)]`}>
              <label className={labelClasses} htmlFor="zipcode">{intl.formatMessage({ id: 'CEP' })}</label>
              <MaskedInput className={inputClasses} type="text" name="address[zipcode]" id="zipcode" data-cy="zipcode" value={client?.address?.zipcode || ''} onChange={onChange} guide={false} mask={zipcodeMask} />
            </div>
          </div>

          <div className={fieldRowClasses}>
            <div className={`${fieldColClasses} w-[calc(16.66%-8.33px)]`}>
              <label className={labelClasses} htmlFor="state">{intl.formatMessage({ id: 'STATE' })}</label>
              {useTextInputForAddress ? (
                <input className={inputClasses} type="text" name="address[state]" id="state" data-cy="state" value={client?.address?.state || ''} onChange={onChange} required />
              ) : (
                <select className={inputClasses} name="address[state_id]" id="state" data-cy="state" value={client?.address?.state_id || ''} onChange={onChange}>
                  <option value="" disabled />
                  {states.map(state => (
                    <option value={state.id} key={state.id}>{state.name}</option>
                  ))}
                </select>
              )}
            </div>
            <div className={`${fieldColClasses} w-[calc(41.66%-5.83px)]`}>
              <label className={labelClasses} htmlFor="city">{intl.formatMessage({ id: 'CITY' })}</label>
              {useTextInputForAddress ? (
                <input className={inputClasses} type="text" name="address[city]" id="city" data-cy="city" value={client?.address?.city || ''} onChange={onChange} required />
              ) : (
                <select className={inputClasses} name="address[city_id]" id="city" data-cy="city" value={client?.address?.city_id || ''} onChange={onChange} disabled={!cities.length}>
                  <option value="" disabled />
                  {cities.map(city => (
                    <option value={city.id} key={city.id}>{city.name}</option>
                  ))}
                </select>
              )}
            </div>
            <div className={`${fieldColClasses} w-[calc(41.66%-5.83px)]`}>
              <label className={labelClasses} htmlFor="neighborhood">{intl.formatMessage({ id: 'NEIGHBORHOOD' })}</label>
              {useTextInputForAddress ? (
                <input className={inputClasses} type="text" name="address[neighborhood]" id="neighborhood" data-cy="neighborhood" value={client?.address?.neighborhood || ''} onChange={onChange} />
              ) : (
                <select className={inputClasses} name="address[neighborhood_id]" id="neighborhood" data-cy="neighborhood" value={client?.address?.neighborhood_id || ''} onChange={onChange} disabled={!neighborhoods.length}>
                  <option value="" disabled />
                  {neighborhoods.map(neighborhood => (
                    <option value={neighborhood.id} key={neighborhood.id}>{neighborhood.name}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className={fieldRowClasses}>
            <div className={`${fieldColClasses} w-[calc(50%-5px)]`}>
              <label className={labelClasses} htmlFor="street">{intl.formatMessage({ id: 'STREET' })}</label>
              <input className={inputClasses} type="text" name="address[street]" id="street" data-cy="street" value={client?.address?.street || ''} onChange={onChange} />
            </div>
            <div className={`${fieldColClasses} w-[calc(25%-7.5px)]`}>
              <label className={labelClasses} htmlFor="number">{intl.formatMessage({ id: 'NUMBER' })}</label>
              <input className={inputClasses} type="text" name="address[number]" id="number" data-cy="number" value={client?.address?.number || ''} onChange={onChange} />
            </div>
            <div className={`${fieldColClasses} w-[calc(25%-7.5px)]`}>
              <label className={labelClasses} htmlFor="complement">{intl.formatMessage({ id: 'COMPLEMENT' })}</label>
              <input className={inputClasses} type="text" name="address[complement]" id="complement" data-cy="complement" value={client?.address?.complement || ''} onChange={onChange} />
            </div>
          </div>

          <SectionTitle icon={<AddressIcon />}>
            {intl.formatMessage({ id: 'BILLING_ADDRESS' })}
          </SectionTitle>

          <div className="flex flex-row items-center mt-[1.4em] mb-[2em] px-[3.46em]">
            <div className="flex items-center">
              <Radio name="showBillingAddress" className="mr-[0.5em]" id="showBillingAddressNo" data-cy="showBillingAddressNo" value="false" onChange={onChange} checked={showBillingAddress === false} />
              <label htmlFor="showBillingAddressNo" className="m-0 text-[0.93875em] font-medium text-[#393939] cursor-pointer">{intl.formatMessage({ id: 'SAME_ADDRESS' })}</label>
            </div>
            <div className="flex items-center ml-[2em]">
              <Radio name="showBillingAddress" className="mr-[0.5em]" id="showBillingAddressYes" data-cy="showBillingAddressYes" value="true" onChange={onChange} checked={showBillingAddress === true} />
              <label htmlFor="showBillingAddressYes" className="m-0 text-[0.93875em] font-medium text-[#393939] cursor-pointer">{intl.formatMessage({ id: 'DIFFERENT_ADDRESS' })}</label>
            </div>
          </div>

          {showBillingAddress && (
            <>
              <div className={fieldRowClasses}>
                <div className={`${fieldColClasses} w-[calc(50%-5px)]`}>
                  <label className={labelClasses} htmlFor="billing_address_zipcode">{intl.formatMessage({ id: 'CEP' })}</label>
                  <MaskedInput className={inputClasses} type="text" name="billing_address[zipcode]" id="billing_address_zipcode" data-cy="billing_address_zipcode" value={client?.billing_address?.zipcode || ''} onChange={onChange} guide={false} mask={zipcodeMask} />
                </div>
              </div>

              <div className={fieldRowClasses}>
                <div className={`${fieldColClasses} w-[calc(16.66%-8.33px)]`}>
                  <label className={labelClasses} htmlFor="billing_address_state_id">{intl.formatMessage({ id: 'STATE' })}</label>
                  {useTextInputForAddress ? (
                    <input className={inputClasses} type="text" name="billing_address[state]" id="billing_address_state_id" data-cy="billing_address_state_id" value={client?.billing_address?.state || ''} onChange={onChange} required />
                  ) : (
                    <select className={inputClasses} name="billing_address[state_id]" id="billing_address_state_id" data-cy="billing_address_state_id" value={client?.billing_address?.state_id || ''} onChange={onChange}>
                      <option value="" disabled />
                      {states.map(state => (
                        <option value={state.id} key={state.id}>{state.name}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div className={`${fieldColClasses} w-[calc(41.66%-5.83px)]`}>
                  <label className={labelClasses} htmlFor="billing_address_city_id">{intl.formatMessage({ id: 'CITY' })}</label>
                  {useTextInputForAddress ? (
                    <input className={inputClasses} type="text" name="billing_address[city]" id="billing_address_city_id" data-cy="billing_address_city_id" value={client?.billing_address?.city || ''} onChange={onChange} required />
                  ) : (
                    <select className={inputClasses} name="billing_address[city_id]" id="billing_address_city_id" data-cy="billing_address_city_id" value={client?.billing_address?.city_id || ''} onChange={onChange} disabled={!billingCities.length}>
                      <option value="" disabled />
                      {billingCities.map(billingCity => (
                        <option value={billingCity.id} key={billingCity.id}>{billingCity.name}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div className={`${fieldColClasses} w-[calc(41.66%-5.83px)]`}>
                  <label className={labelClasses} htmlFor="billing_address_neighborhood">{intl.formatMessage({ id: 'NEIGHBORHOOD' })}</label>
                  {useTextInputForAddress ? (
                    <input className={inputClasses} type="text" name="billing_address[neighborhood]" id="billing_address_neighborhood_id" data-cy="billing_address_neighborhood_id" value={client?.billing_address?.neighborhood || ''} onChange={onChange} />
                  ) : (
                    <select className={inputClasses} name="billing_address[neighborhood_id]" id="billing_address_neighborhood_id" data-cy="billing_address_neighborhood_id" value={client?.billing_address?.neighborhood_id || ''} onChange={onChange} disabled={!billingNeighborhoods.length}>
                      <option value="" disabled />
                      {billingNeighborhoods.map(billingNeighborhood => (
                        <option value={billingNeighborhood.id} key={billingNeighborhood.id}>{billingNeighborhood.name}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              <div className={fieldRowClasses}>
                <div className={`${fieldColClasses} w-[calc(50%-5px)]`}>
                  <label className={labelClasses} htmlFor="billing_address_street">{intl.formatMessage({ id: 'STREET' })}</label>
                  <input className={inputClasses} type="text" name="billing_address[street]" id="billing_address_street" data-cy="billing_address_street" value={client?.billing_address?.street || ''} onChange={onChange} />
                </div>
                <div className={`${fieldColClasses} w-[calc(25%-7.5px)]`}>
                  <label className={labelClasses} htmlFor="billing_address_number">{intl.formatMessage({ id: 'NUMBER' })}</label>
                  <input className={inputClasses} type="text" name="billing_address[number]" id="billing_address_number" data-cy="billing_address_number" value={client?.billing_address?.number || ''} onChange={onChange} />
                </div>
                <div className={`${fieldColClasses} w-[calc(25%-7.5px)]`}>
                  <label className={labelClasses} htmlFor="billing_address_complement">{intl.formatMessage({ id: 'COMPLEMENT' })}</label>
                  <input className={inputClasses} type="text" name="billing_address[complement]" id="billing_address_complement" data-cy="billing_address_complement" value={client?.billing_address?.complement || ''} onChange={onChange} />
                </div>
              </div>
            </>
          )}

          <SectionTitle icon={contactImage}>
            {intl.formatMessage({ id: 'CONTACT' })}
          </SectionTitle>

          <div className={fieldRowClasses}>
            <div className={`${fieldColClasses} w-full`}>
              <label className={labelClasses} htmlFor="assignmentEmailRule">{intl.formatMessage({ id: 'EMAIL_FOR_ORDER_ASSIGNMENT_RULE' })}</label>
              <select className={inputClasses} id="assignmentEmailRule" data-cy="assignmentEmailRule" name="contact[assignment_email_rule]" value={client?.contact?.assignment_email_rule || ''} onChange={onChange}>
                <option value="CREATOR">{intl.formatMessage({ id: 'ORDER_ASSIGNMENT_RULE_CREATOR' })}</option>
                <option value="CUSTOM">{intl.formatMessage({ id: 'ORDER_ASSIGNMENT_RULE_CUSTOM' })}</option>
                <option value="RESPONSIBLE">{intl.formatMessage({ id: 'ORDER_ASSIGNMENT_RULE_RESPONSIBLE' })}</option>
                <option value="NONE">{intl.formatMessage({ id: 'ORDER_ASSIGNMENT_RULE_NONE' })}</option>
              </select>
            </div>
          </div>

          <div className={fieldRowClasses}>
            <div className={`${fieldColClasses} w-full`}>
              <label className={labelClasses} htmlFor="assignmentEmail">{intl.formatMessage({ id: 'EMAIL_FOR_ORDER_ASSIGNMENT' })}</label>
              <input className={`${inputClasses} disabled:bg-[#efefef] disabled:cursor-not-allowed`} id="assignmentEmail" data-cy="assignmentEmail" type="text" name="contact[assignment_email]" disabled={client?.contact?.assignment_email_rule !== 'CUSTOM'} value={client?.contact?.assignment_email || ''} onChange={onChange} />
            </div>
          </div>

          <div className={fieldRowClasses}>
            <div className={`${fieldColClasses} w-[calc(50%-5px)]`}>
              <label className={labelClasses} htmlFor="phone">{intl.formatMessage({ id: 'PHONE' })}</label>
              <MaskedInput className={inputClasses} type="text" name="contact[phone]" id="phone" data-cy="phone" value={client?.contact?.phone || ''} onChange={onChange} guide={false} mask={value => (value.length >= cellphoneMaskLength ? cellphoneMask : phoneMask)} />
            </div>
            <div className={`${fieldColClasses} w-[calc(50%-5px)]`}>
              <label className={labelClasses} htmlFor="cellphone">{intl.formatMessage({ id: 'CELLPHONE' })}</label>
              <MaskedInput className={inputClasses} type="text" name="contact[cellphone]" id="cellphone" data-cy="cellphone" value={client?.contact?.cellphone || ''} onChange={onChange} guide={false} mask={value => (value.length >= cellphoneMaskLength ? cellphoneMask : phoneMask)} />
            </div>
          </div>

          <SectionTitle icon={documentImage}>
            {intl.formatMessage({ id: 'ORDER_PAGE' })}
          </SectionTitle>

          <div className={fieldClasses}>
            <label className={labelClasses} htmlFor="expiration_time">{intl.formatMessage({ id: 'DEADLINE_FOR_RECORDING_DIVERGENCES_IN_DAYS' })}</label>
            <input className={inputClasses} type="number" name="hotsite_configuration[expiration_time]" id="expiration_time" data-cy="expiration_time" min="0" value={client?.hotsite_configuration?.expiration_time || ''} onChange={onChange} />
          </div>

          <div className={fieldClasses}>
            <label className={labelClasses} htmlFor="cc_email">{intl.formatMessage({ id: 'ADMINISTRATOR_EMAIL' })}</label>
            <input className={inputClasses} type="email" name="hotsite_configuration[cc_email]" id="cc_email" data-cy="cc_email" value={client?.hotsite_configuration?.cc_email || ''} onChange={onChange} />
          </div>

          <SectionTitle icon={documentImage}>
            {intl.formatMessage({ id: 'FRANCHISES' })}
          </SectionTitle>

          <div className={fieldClasses}>
            <Select
              id="commercialFranchiseesSelect"
              name="franchisee"
              data-cy="franchisee"
              placeholder={`${intl.formatMessage({ id: 'SELECT_ONE_FRANCHISE_MESSAGE' })}*`}
              classNamePrefix="select"
              styles={selectStyles}
              isClearable
              required
              options={franchisees}
              noOptionsMessage={() => intl.formatMessage({ id: 'NO_FRANCHISES_AVAILABLE_MESSAGE' })}
              isDisabled={!franchisees.length}
              onChange={onChangeFranchisee}
            />
          </div>

          <div className="flex justify-center items-center mt-[1.75em] pt-[1.75em] border-t border-[#eee] space-x-4">
            <Link to="/commercial/clients" className={`inline-flex items-center justify-center min-w-[140px] h-[36px] px-[1.2rem] border-0 rounded-[3px] bg-[#c1c1c1] text-[0.875rem] font-bold text-white transition-opacity hover:opacity-75 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${busy ? 'pointer-events-none opacity-50' : ''}`}>
              {intl.formatMessage({ id: 'CANCEL' })}
            </Link>
            <button type="submit" className={`inline-flex items-center justify-center min-w-[140px] h-[36px] px-[1.2rem] border-0 rounded-[3px] bg-[#97a500] text-[0.875rem] font-bold text-white transition-opacity hover:opacity-75 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed`} disabled={busy}>
              {intl.formatMessage({ id: 'SAVE' })}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
};

ClientRegistrationFormComponent.propTypes = {
  busy: PropTypes.bool.isRequired,
  client: PropTypes.object,
  franchisees: PropTypes.array,
  intl: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  showBillingAddress: PropTypes.bool,
  origins: PropTypes.array.isRequired,
  classifications: PropTypes.array.isRequired,
  zipcodeMask: PropTypes.array.isRequired,
  states: PropTypes.array.isRequired,
  cities: PropTypes.array.isRequired,
  billingCities: PropTypes.array.isRequired,
  neighborhoods: PropTypes.array.isRequired,
  billingNeighborhoods: PropTypes.array.isRequired,
  type: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onChangeFranchisee: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  cnpjMask: PropTypes.array.isRequired,
  cpfOnlyMask: PropTypes.array.isRequired,
  phoneMask: PropTypes.array.isRequired,
  cellphoneMask: PropTypes.array.isRequired,
  cellphoneMaskLength: PropTypes.number.isRequired,
  useTextInputForAddress: PropTypes.bool,
};

ClientRegistrationFormComponent.defaultProps = {
  client: null,
  franchisees: [],
  showBillingAddress: false,
  type: null,
  useTextInputForAddress: false,
};

export default ClientRegistrationFormComponent;
